/**
 * Pricing utilities for ad campaigns
 * Extracted from campaign-wizard.tsx for reusability
 */

import { differenceInDays } from 'date-fns';
import { formatPrice } from '@/lib/utils';
import { EUR_TO_USD, EUR_TO_KMF, type Currency } from '@/lib/utils/constants';
import type { AdType, AdTypeConfig, AdPriceUnit } from '../types/ads.types';

// ============ Default Configs (Fallback) ============

export const DEFAULT_CONFIGS: Record<AdType, Partial<AdTypeConfig>> = {
  push_shop_subscribers: { priceEur: 0.003, priceUnit: 'per_1000' as AdPriceUnit, minBudgetEur: 3 },
  push_product_likers: { priceEur: 0.004, priceUnit: 'per_1000' as AdPriceUnit, minBudgetEur: 4 },
  push_single_product_likers: { priceEur: 0.002, priceUnit: 'per_1000' as AdPriceUnit, minBudgetEur: 2 },
  homepage_carousel: { priceEur: 15, priceUnit: 'per_day' as AdPriceUnit, minBudgetEur: 15 },
  sponsored_product: { priceEur: 0.05, priceUnit: 'per_click' as AdPriceUnit, minBudgetEur: 5 },
  featured_shop: { priceEur: 20, priceUnit: 'per_week' as AdPriceUnit, minBudgetEur: 20 },
  category_banner: { priceEur: 8, priceUnit: 'per_day' as AdPriceUnit, minBudgetEur: 8 },
  push_all_users: { priceEur: 5, priceUnit: 'per_1000' as AdPriceUnit, minBudgetEur: 5 },
};

// ============ Type Categories ============

export const NOTIFICATION_TYPES: AdType[] = [
  'push_shop_subscribers',
  'push_product_likers',
  'push_single_product_likers',
];

export const PRODUCT_REQUIRED_TYPES: AdType[] = [
  'sponsored_product',
  'homepage_carousel',
  'push_product_likers',
  'push_single_product_likers',
  'category_banner',
];

export const PER_DAY_TYPES: AdType[] = ['homepage_carousel', 'category_banner'];

export const BANNER_TYPES: AdType[] = ['homepage_carousel', 'category_banner'];

// ============ Type Checkers ============

export function isNotificationType(type: AdType): boolean {
  return NOTIFICATION_TYPES.includes(type);
}

export function requiresProducts(type: AdType): boolean {
  return PRODUCT_REQUIRED_TYPES.includes(type);
}

export function isPerDayType(type: AdType): boolean {
  return PER_DAY_TYPES.includes(type);
}

export function isBannerType(type: AdType): boolean {
  return BANNER_TYPES.includes(type);
}

export function isCpcType(type: AdType): boolean {
  return type === 'sponsored_product';
}

export function isFeaturedShopType(type: AdType): boolean {
  return type === 'featured_shop';
}

// ============ Currency Conversion ============

export function toEur(amount: number, fromCurrency: Currency): number {
  if (fromCurrency === 'EUR') return amount;
  if (fromCurrency === 'USD') return amount / EUR_TO_USD;
  if (fromCurrency === 'KMF') return amount / EUR_TO_KMF;
  return amount;
}

export function fromEur(amountEur: number, toCurrency: Currency): number {
  if (toCurrency === 'EUR') return amountEur;
  if (toCurrency === 'USD') return amountEur * EUR_TO_USD;
  if (toCurrency === 'KMF') return amountEur * EUR_TO_KMF;
  return amountEur;
}

// ============ Config Retrieval ============

/**
 * Get config for a specific type from server configs or fallback
 */
export function getTypeConfig(
  type: AdType,
  serverConfigs: AdTypeConfig[] | undefined
): AdTypeConfig | null {
  if (serverConfigs) {
    const config = serverConfigs.find((c) => c.type === type);
    if (config) return config;
  }

  // Return fallback as partial config
  const fallback = DEFAULT_CONFIGS[type];
  if (fallback) {
    return {
      id: 0,
      type,
      enabled: true,
      priceEur: fallback.priceEur ?? 0,
      priceUsd: (fallback.priceEur ?? 0) * EUR_TO_USD,
      priceKmf: (fallback.priceEur ?? 0) * EUR_TO_KMF,
      priceUnit: fallback.priceUnit ?? 'per_click',
      minBudgetEur: fallback.minBudgetEur ?? 5,
      minBudgetUsd: (fallback.minBudgetEur ?? 5) * EUR_TO_USD,
      minBudgetKmf: (fallback.minBudgetEur ?? 5) * EUR_TO_KMF,
      updatedAt: new Date().toISOString(),
    };
  }

  return null;
}

// ============ Price Display ============

/**
 * Get price display string for a campaign type
 */
export function getPriceDisplayForType(
  _type: AdType,
  config: AdTypeConfig | null,
  currency: Currency
): string {
  if (!config) return '-';

  const price =
    currency === 'EUR'
      ? config.priceEur
      : currency === 'USD'
      ? config.priceUsd
      : config.priceKmf;

  switch (config.priceUnit) {
    case 'per_click':
      return `À partir de ${formatPrice(price, currency)}/clic`;
    case 'per_1000':
      return `${formatPrice(price, currency)}/1000 notifs`;
    case 'per_day':
      return `${formatPrice(price, currency)}/jour`;
    case 'per_week':
      return `${formatPrice(price, currency)}/semaine`;
    case 'per_month':
      return `${formatPrice(price, currency)}/mois`;
    case 'fixed':
      return `${formatPrice(price, currency)}`;
    default:
      return `${formatPrice(price, currency)}`;
  }
}

// ============ Cost Calculation ============

export interface CostCalculation {
  calculatedCost: number | null;
  isBelowMinimum: boolean;
  minBudgetInCurrency: number;
}

/**
 * Calculate the cost of a campaign based on its type and parameters
 */
export function calculateCampaignCost(
  type: AdType,
  config: AdTypeConfig | null,
  currency: Currency,
  params: {
    audienceCount?: number;
    startDate?: string;
    endDate?: string;
    budget?: number;
  }
): CostCalculation {
  const { audienceCount = 0, startDate, endDate, budget = 0 } = params;

  if (!config) {
    return { calculatedCost: null, isBelowMinimum: false, minBudgetInCurrency: 0 };
  }

  // Notification types - price per 1000 notifications
  if (isNotificationType(type)) {
    const costEur = (audienceCount / 1000) * config.priceEur;
    const minInCurrency = fromEur(config.minBudgetEur, currency);
    const costInCurrency = fromEur(costEur, currency);
    return {
      calculatedCost: costInCurrency,
      isBelowMinimum: costEur < config.minBudgetEur,
      minBudgetInCurrency: minInCurrency,
    };
  }

  // Per-day types (carousel, category banner)
  if (isPerDayType(type) && startDate && endDate) {
    const days = differenceInDays(new Date(endDate), new Date(startDate)) + 1;
    const costEur = days * config.priceEur;
    return {
      calculatedCost: fromEur(costEur, currency),
      isBelowMinimum: false,
      minBudgetInCurrency: 0,
    };
  }

  // Featured shop - per week
  if (isFeaturedShopType(type) && startDate && endDate) {
    const days = differenceInDays(new Date(endDate), new Date(startDate)) + 1;
    const weeks = Math.ceil(days / 7);
    const costEur = weeks * config.priceEur;
    return {
      calculatedCost: fromEur(costEur, currency),
      isBelowMinimum: false,
      minBudgetInCurrency: 0,
    };
  }

  // CPC types - user-defined budget
  return { calculatedCost: budget, isBelowMinimum: false, minBudgetInCurrency: 0 };
}

// ============ Budget Presets ============

/**
 * Get budget presets based on currency
 */
export function getBudgetPresets(currency: Currency): number[] {
  if (currency === 'KMF') {
    return [2500, 5000, 10000, 25000, 50000];
  } else if (currency === 'USD') {
    return [5, 10, 25, 50, 100];
  }
  return [5, 10, 20, 50, 100];
}

/**
 * Get budget limits based on currency
 */
export function getBudgetLimits(currency: Currency): {
  min: number;
  max: number;
  step: number;
} {
  if (currency === 'KMF') {
    return { min: 1000, max: 500000, step: 500 };
  }
  return { min: 1, max: 1000, step: 1 };
}

/**
 * Get minimum bid amount based on config and currency
 */
export function getMinBidAmount(config: AdTypeConfig | null, currency: Currency): number {
  if (config && config.priceUnit === 'per_click') {
    return fromEur(config.priceEur, currency);
  }
  return currency === 'KMF' ? 25 : 0.05;
}

// ============ Budget Conversion for API ============

/**
 * Convert budget to all currencies for API submission
 */
export function convertBudgetToAllCurrencies(
  budget: number,
  fromCurrency: Currency
): { budgetEur: number; budgetUsd: number; budgetKmf: number } {
  const budgetEur = toEur(budget, fromCurrency);
  return {
    budgetEur: Math.round(budgetEur * 100) / 100,
    budgetUsd: Math.round(fromEur(budgetEur, 'USD') * 100) / 100,
    budgetKmf: Math.round(fromEur(budgetEur, 'KMF')),
  };
}
